// A KEZDŐLAP SZERKESZTŐ-NÉZETE: a csempék egy ugyanabban a rácsban, mint a nézet (`DASHBOARD_GRID_CLASS`, VALÓDI szélességekkel), összecsukott
// előnézettel — húzással ÉS gombokkal rendezhetők.
//
// HÚZÁS: @dnd-kit — PointerSensor (6 px küszöb) · TouchSensor (250 ms késleltetés, hogy a görgetés ne induljon
// húzásnak) · KeyboardSensor (Tab a fogantyúra, Szóköz felemel, nyilak mozgatnak, Szóköz letesz, Esc visszavon).
// A képernyőolvasó-bejelentések magyarul/angolul, i18n-kulcsokból.
import { dashboardTileDef, type DashboardTileEntry, type DashboardTileSize } from '@atlas/shared';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components/shared/empty-state';
import { TileEditFrame } from '@/features/dashboard/components/tile-edit-frame';
import { DASHBOARD_GRID_CLASS } from '@/features/dashboard/layout/spans';

export interface DashboardEditorProps {
  tiles: DashboardTileEntry[];
  onMove: (from: number, to: number) => void;
  onRemove: (id: string) => void;
  onResize: (id: string, size: DashboardTileSize) => void;
  /** A frissen hozzáadott csempe: ide görget a nézet, és a fogantyújára ugrik a fókusz. */
  focusId: string | null;
}

export function DashboardEditor({
  tiles,
  onMove,
  onRemove,
  onResize,
  focusId,
}: DashboardEditorProps): JSX.Element {
  const { t } = useTranslation();
  const ids = useMemo(() => tiles.map((x) => x.id), [tiles]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const titleOf = (id: UniqueIdentifier): string => {
    const def = dashboardTileDef(String(id));
    return def ? t(def.titleKey) : String(id);
  };
  const positionOf = (id: UniqueIdentifier): number => ids.indexOf(String(id)) + 1;

  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      t('dashboard.editor.a11y.lifted', {
        tile: titleOf(active.id),
        position: positionOf(active.id),
      }),
    onDragOver: ({ active, over }) =>
      over
        ? t('dashboard.editor.a11y.moved', {
            tile: titleOf(active.id),
            position: positionOf(over.id),
          })
        : undefined,
    onDragEnd: ({ active, over }) =>
      t('dashboard.editor.a11y.dropped', {
        tile: titleOf(active.id),
        position: positionOf(over ? over.id : active.id),
      }),
    onDragCancel: ({ active }) =>
      t('dashboard.editor.a11y.cancelled', { tile: titleOf(active.id) }),
  };

  const onDragEnd = ({ active, over }: DragEndEvent): void => {
    if (!over || active.id === over.id) return;
    onMove(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
  };

  useEffect(() => {
    if (focusId === null) return;
    const handle = document.querySelector<HTMLElement>(`[data-tile-handle="${focusId}"]`);
    handle?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    handle?.focus();
  }, [focusId]);

  if (tiles.length === 0) {
    return (
      <EmptyState
        icon={Squares2X2Icon}
        title={t('dashboard.editor.emptyTitle')}
        description={t('dashboard.editor.emptyDescription')}
      />
    );
  }

  return (
    <DndContext
      id="dashboard-editor"
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement]}
      accessibility={{
        announcements,
        screenReaderInstructions: { draggable: t('dashboard.editor.a11y.instructions') },
      }}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <ol className={DASHBOARD_GRID_CLASS} aria-label={t('dashboard.editor.listLabel')}>
          {tiles.map((entry, index) => (
            <TileEditFrame
              key={entry.id}
              entry={entry}
              index={index}
              count={tiles.length}
              onMoveUp={() => onMove(index, index - 1)}
              onMoveDown={() => onMove(index, index + 1)}
              onRemove={() => onRemove(entry.id)}
              onResize={(size) => onResize(entry.id, size)}
            />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
}
